import React, { useEffect, useMemo } from 'react';
import { useAppSelector } from 'hooks';
import { Form, Formik } from 'formik';
import dayjs from 'dayjs';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, Modal } from '~components';
import { Fields } from '~components/form-fields';

import {
  useCreateCarePlanReviewMutation,
  useGetReviewTriggerTypesQuery,
  useGetReviewTypesQuery,
} from '~clients/actions/carePlans';

import {
  buildReviewCreateBody,
  reviewFormFields,
  reviewFormSchema,
  ReviewFormValues,
} from '~clients/forms/care-plans/reviewForm';

import { useFieldValidationHighlights } from '~clients/hooks/useFieldValidationHighlights';

import { selectAuthenticatedUser } from '~main/selectors/login';

interface ComponentProps {
  planId: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

// The index signature satisfies the Fields component's values contract.
interface FormValues extends ReviewFormValues {
  [key: string]: string | number | boolean | object | null;
}

/**
 * "Add Review" modal (create-only; reviews are append-only). Sends review_due_date
 * (defaulting to 12 months after the review date when blank), which the API stamps onto
 * the parent plan. Mirrors the Assessed Need / Goal form modals.
 */
const ReviewFormModal: React.FC<ComponentProps> = ({ planId, isOpen, setIsOpen }) => {
  const { showToast } = useToast();
  const authUser = useAppSelector(selectAuthenticatedUser);
  const { data: reviewTypes } = useGetReviewTypesQuery();
  const { data: triggerTypes } = useGetReviewTriggerTypesQuery();
  const [createReview, { isLoading: isSaving, error, reset }] = useCreateCarePlanReviewMutation();

  // Prefill the review date (today) and reviewer (the logged-in user); both overridable.
  const initialValues: FormValues = useMemo(
    () => ({
      review_type_id: '',
      review_date: dayjs().format('YYYY-MM-DD'),
      reviewer_id: authUser?.id ?? null,
      trigger_type_id: '',
      attendees_description: '',
      summary: '',
      outcome: '',
      review_due_date: '',
    }),
    [authUser?.id],
  );

  const triggeredTypeId = useMemo(
    () => (reviewTypes ?? []).find(({ tag }) => tag === 'triggered')?.id,
    [reviewTypes],
  );

  const definitions = useMemo(() => {
    const fields = reviewFormFields({
      reviewTypeOptions: (reviewTypes ?? []).map(({ id, name }) => ({ id, name })),
      triggerOptions: (triggerTypes ?? []).map(({ id, name }) => ({ id, name })),
      triggeredTypeId,
    });
    return Object.entries(fields).map(([key, props]) => ({ key, ...props }));
  }, [reviewTypes, triggerTypes, triggeredTypeId]);

  const validationSchema = useMemo(() => reviewFormSchema(triggeredTypeId), [triggeredTypeId]);
  const { invalidFields, validate } = useFieldValidationHighlights(validationSchema);

  // The instance is reused across close/reopen, so clear a failed attempt's error on close.
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const onSubmit = async (values: FormValues) => {
    try {
      await createReview({ planId, ...buildReviewCreateBody(values, triggeredTypeId) }).unwrap();
      showToast('success', 'Review recorded');
      setIsOpen(false);
    } catch {
      // Surfaced through the mutation error below.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Add Review"
      includeFooter={false}
      bodyClassName="care-plan-form-body"
      size="xl"
    >
      {error !== undefined && <ErrorResponse dataCy="care-plan-review-form-error" message={getErrorMessage(error)} />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validate={validate}
        validateOnMount={false}
        validateOnChange={invalidFields.length > 0}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, setFieldTouched, touched, dirty }) => (
          <Form>
            <Fields
              definitions={definitions}
              values={values}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              touched={touched}
              invalidFields={invalidFields}
            />
            <div className="care-plan-review-form-actions">
              <Button size="sm" color="warning" dataCy="care-plan-review-cancel" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                color="success"
                disabled={!dirty}
                loading={isSaving}
                dataCy="care-plan-review-save"
              >
                Add Review
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ReviewFormModal;
